import { API_URL_REFRESH_TOKEN, AppActionPermissions, DISPLAY_PAGES } from '../constants'
import Cookies from 'js-cookie'
import { api } from '../api'
import { AuthContext, AuthenticationStatus, AuthStateAndStatus, LoginEvent, CurrentUser } from './AuthenticationContext'
import history from '../history'
import amplitude, { Identify, setUserIdSafe, ensureAmplitudeInit } from '../../amplitude'

export type LoginResponse = {
  userId: number | null
  userEmail: string
  firstName: string
  lastName: string
  accessToken: string
  accessTokenExpMs: number
  sidebarItems: DISPLAY_PAGES[] | Array<string>
  developerOptions: boolean
  temperatureInC: boolean
  permissions: AppActionPermissions
}

type ApplyAmplitudeUserContextParams = Pick<LoginResponse, 'userId' | 'userEmail' | 'firstName' | 'lastName'> & {
  trackEvent?: {
    name: string
    properties?: Record<string, unknown>
  }
}

export const applyAmplitudeUserContext = ({
  userId,
  userEmail,
  firstName,
  lastName,
  trackEvent,
}: ApplyAmplitudeUserContextParams) => {
  ensureAmplitudeInit();

  // ✅ Amplitude identity = email (if present)
  const emailId = (userEmail ?? '').trim();
  setUserIdSafe(emailId || undefined); // clears identity if no email

  // Build Identify properties
  const identify = new Identify();

  // Store your numeric id as a user *property*.
  // You asked for the field name `user_id` — that’s fine.
  if (userId != null) identify.set('user_id', String(userId));

  if (emailId)     identify.set('email', emailId);
  if (firstName)   identify.set('firstName', firstName);
  if (lastName)    identify.set('lastName', lastName);
  const full = `${firstName ?? ''} ${lastName ?? ''}`.trim();
  if (full)        identify.set('full_name', full);
  if (process.env.NODE_ENV) identify.set('env', process.env.NODE_ENV);

  amplitude.identify(identify);

  if (trackEvent?.name) {
    amplitude.track(trackEvent.name, {
      userId,      // your numeric id (optional to keep for troubleshooting)
      userEmail,   // email (same as identity)
      ...(trackEvent.properties ?? {}),
    });
  }
};


export const UNAUTH_ALLOWED_PATHS = ['/email-unsubscribed', '/set-new-password', '/initial-password', '/login', '/set-new-pin']

export const userLogout = () => {
  Cookies.remove(LoginEvent.loginCookie)
  localStorage.removeItem(LoginEvent.logout)
  const currentPath = history.location.pathname
  setUserIdSafe(undefined);
  if (!UNAUTH_ALLOWED_PATHS.includes(currentPath)) {
    history.push('/login')
  }
}

export const displayPagesChanged = (currSideBar: string[], respSideBar: string[]) => {
  if (currSideBar.length != respSideBar.length) return true
  const respSet = new Set(respSideBar)
  const respEvery: boolean = currSideBar.every((page) => respSet.has(page))
  if (!respEvery) return true
  const currSet = new Set(currSideBar)
  const currEvery: boolean = respSideBar.every((page) => currSet.has(page))
  if (!currEvery) return true
  return false
}

export const checkTokenBeforeAppLaunch = async (): Promise<AuthStateAndStatus> => {
  try {
    if (Cookies.get(LoginEvent.loginCookie)) {
      const initializerLoginResponse = await api
        .noAuth()
        .url(`${API_URL_REFRESH_TOKEN}`)
        .options({ credentials: 'include', mode: 'cors', cookie: Cookies.get(LoginEvent.loginCookie) })
        .post()
        .json()
      if (initializerLoginResponse.accessToken) {
        const {
          userId,
          userEmail,
          firstName,
          lastName,
          accessToken,
          accessTokenExpMs,
          sidebarItems,
          developerOptions,
          temperatureInC,
          permissions,
        } = initializerLoginResponse
        
        /* const amplitudeUserId = userId !== null ? String(userId) : userEmail
        amplitude.setUserId(amplitudeUserId)

        const identify = new Identify()
          .set('user_id', amplitudeUserId)
          .set('email', userEmail)
          .set('firstName', firstName)
          .set('lastName', lastName)
          .set('full_name', `${firstName} ${lastName}`.trim())
          .set('env', process.env.NODE_ENV)
        amplitude.identify(identify)

        amplitude.track('Auto Login Refreshed', { */
        applyAmplitudeUserContext({
          userId,
          userEmail,
          firstName,
          lastName,
          trackEvent: {
            name: 'Auto Login Refreshed',
          },
        })

        api.setToken(accessToken)
        Cookies.set(LoginEvent.loginCookie, AuthenticationStatus.loggedIn, {
          sameSite: 'None', // Allows cross-site cookies
          secure: true, // Required for cross-site cookies in Chrome
        })
        localStorage.setItem(LoginEvent.logout, AuthenticationStatus.loggedOut + Math.random())
        
        const initialAuthState: AuthStateAndStatus = {
          authState: {
            currentUser: { userId, userEmail, firstName, lastName } as CurrentUser,
            accessToken,
            currentContext: AuthContext.loggedIn,
            tokenExpiration: accessTokenExpMs,
            sidebarItems,
            displayPages: developerOptions ? [...sidebarItems, DISPLAY_PAGES.ITEM_DEVELOPER_OPTIONS] : [...sidebarItems],
            developerOptions,
            temperatureInC,
            initialized: true,
            permissions: permissions,
          },
          success: true,
        }
        return { ...initialAuthState }
      } else {
        Cookies.remove(LoginEvent.loginCookie)
        localStorage.removeItem(LoginEvent.logout)
        const currentPath = history.location.pathname
        if (!UNAUTH_ALLOWED_PATHS.includes(currentPath)) {
          history.push('/login')
        }
      }
    }
  } catch (e) {
    console.warn('Warning, authentication refresh caused an error.')
  }
  return { authState: undefined, success: false }
}